/**
 * @license
 * @copyright Copyright Motili Inc., 2017 All Rights Reserved
 */

import { Component, PureComponent } from 'react';
import PropTypes from 'prop-types';
import { find, get } from 'lodash';

import { LoadingSpinner, Checkbox } from '@motili/ui';
import { FormRow, FormElement } from '../form/FormLayout';
import FormTextView from '../form/FormTextView';
import FormTextArea from '../form/FormTextArea';
import FormSelect from '../form/FormSelect';
import Modal from '../modal/Modal';
import MotiliButton from '../../components-v2/buttons/MotiliButton';
import Config from '../../../Config';
import styles from './DocumentRequirementsPhotos.module.scss';
import { isVideo } from './DocumentRequirementsPhotos/DocumentRequirementsPhotosUploaded';

export class DocumentRequirementsPhotosUploadModal extends Component {
    static propTypes = {
        handleCloseOnClick: PropTypes.func.isRequired,
        handleCancelOnClick: PropTypes.func.isRequired,
        handleUploadOnClick: PropTypes.func,
        photoTypeRequirements: PropTypes.arrayOf(
            PropTypes.shape({
                id: PropTypes.number.isRequired,
                display: PropTypes.string.isRequired,
                requirement: PropTypes.shape({
                    id: PropTypes.string.isRequired,
                }),
            })
        ),
        photos: PropTypes.arrayOf(
            PropTypes.shape({
                id: PropTypes.number.isRequired,
                meetsClientRequirement: PropTypes.bool,
                metClientRequirementId: PropTypes.number,
                requirementTypeId: PropTypes.string,
                requirement: PropTypes.shape({
                    photo: PropTypes.object.isRequired,
                    uploadedBy: PropTypes.string,
                    description: PropTypes.string,
                    fileName: PropTypes.string,
                    url: PropTypes.string,
                    mimeType: PropTypes.string,
                }),
            })
        ),
        photoTypes: PropTypes.arrayOf(
            PropTypes.shape({
                id: PropTypes.string.isRequired,
                display: PropTypes.string.isRequired,
            })
        ).isRequired,
        fileName: PropTypes.string.isRequired,
        imgSrc: PropTypes.string,
        mimeType: PropTypes.string.isRequired,
        currentPhotoTypeId: PropTypes.string,
        currentRequiresPullAndClean: PropTypes.bool,
        action: PropTypes.string,
    };

    static defaultProps = {};

    constructor(props) {
        super(props);
        this.state = {
            typeId: '',
            description: this.getDefaultDescription(),
            requiresPullAndClean:
                this.props.currentRequiresPullAndClean || false,
            busy: false,
        };
    }

    getDefaultDescription() {
        if (this.props.imgSrc === Config.notApplicablePhoto.url) {
            return Config.notApplicablePhoto.defaultDescription;
        }
        return '';
    }

    getHeader() {
        if (this.props.imgSrc === Config.notApplicablePhoto.url) {
            return 'Upload Not Applicable Photo';
        }
        return 'Upload Photo';
    }

    render() {
        const photoTypes =
            this.props.mimeType === 'application/pdf'
                ? this.props.photoTypes.filter(t => t.pdfSupported)
                : this.props.photoTypes;

        return (
            <Modal
                header={this.getHeader()}
                onCloseClick={this.props.handleCloseOnClick}
                showClose
                footer={
                    <div
                        style={{ display: 'flex', justifyContent: 'flex-end' }}
                    >
                        <MotiliButton
                            text='CANCEL'
                            moStyle='normal'
                            onClick={this.props.handleCancelOnClick}
                            disabled={this.state.busy}
                        />
                        <div style={{ width: '20px' }} />
                        <MotiliButton
                            text='UPLOAD'
                            onClick={() => this.handleUploadOnClick()}
                            busy={this.state.busy}
                            disabled={
                                (!this.state.typeId &&
                                    !this.props.currentPhotoTypeId) ||
                                (this.props.currentPhotoTypeId &&
                                    !photoTypes.some(type => {
                                        return (
                                            type.id ===
                                            this.props.currentPhotoTypeId
                                        );
                                    })) ||
                                this.state.busy
                            }
                        />
                    </div>
                }
            >
                <DocumentRequirementsPhotosModalContent
                    currentPhotoTypeId={this.props.currentPhotoTypeId}
                    photoTypes={photoTypes}
                    photoTypeRequirements={this.props.photoTypeRequirements}
                    photos={this.props.photos}
                    fileName={this.props.fileName}
                    imgSrc={this.props.imgSrc}
                    mimeType={this.props.mimeType}
                    typeId={this.state.typeId}
                    description={this.state.description}
                    disabled={this.state.busy}
                    requiresPullAndClean={this.state.requiresPullAndClean}
                    handleTypeIdOnChange={typeId => this.setState({ typeId })}
                    handleDescriptionOnChange={e =>
                        this.setState({ description: e.target.value })
                    }
                    handlePullAndCleanChange={v =>
                        this.setState({ requiresPullAndClean: v })
                    }
                    action={this.props.action}
                />
            </Modal>
        );
    }

    handleUploadOnClick() {
        this.setState({ busy: true });
        this.props
            .handleUploadOnClick(this.state)
            .catch(() => this.setState({ busy: false }));
    }
}

export class DocumentRequirementsPhotosEditModal extends Component {
    static propTypes = {
        handleCloseOnClick: PropTypes.func.isRequired,
        handleCancelOnClick: PropTypes.func.isRequired,
        handleSaveOnClick: PropTypes.func,
        photoTypeRequirements: PropTypes.arrayOf(
            PropTypes.shape({
                id: PropTypes.number.isRequired,
                display: PropTypes.string.isRequired,
                requirement: PropTypes.shape({
                    id: PropTypes.string.isRequired,
                }),
            })
        ),
        photos: PropTypes.arrayOf(
            PropTypes.shape({
                id: PropTypes.number.isRequired,
                meetsClientRequirement: PropTypes.bool,
                metClientRequirementId: PropTypes.number,
                requirementTypeId: PropTypes.string,
                requirement: PropTypes.shape({
                    photo: PropTypes.object.isRequired,
                    uploadedBy: PropTypes.string,
                    description: PropTypes.string,
                    fileName: PropTypes.string,
                    url: PropTypes.string,
                    mimeType: PropTypes.string,
                }),
            })
        ),
        photoTypes: PropTypes.arrayOf(
            PropTypes.shape({
                id: PropTypes.string.isRequired,
                display: PropTypes.string.isRequired,
            })
        ).isRequired,
        fileName: PropTypes.string.isRequired,
        imgSrc: PropTypes.string,
        mimeType: PropTypes.string.isRequired,
    };

    static defaultProps = {};

    constructor(props) {
        super(props);
        const photo = this.props.photo || {};
        this.state = {
            typeId: photo.typeId || '',
            description: photo.description || '',
            busy: false,
        };
    }

    render() {
        return (
            <Modal
                header='Edit Photo'
                onCloseClick={this.props.handleCloseOnClick}
                showClose
                footer={
                    <div style={{ display: 'flex' }}>
                        <MotiliButton
                            text='CANCEL'
                            moStyle='normal'
                            onClick={this.props.handleCancelOnClick}
                            disabled={this.state.busy}
                        />
                        <div style={{ width: '20px' }} />
                        <MotiliButton
                            text='SAVE'
                            onClick={() => this.handleSaveOnClick()}
                            busy={this.state.busy}
                            disabled={!this.state.typeId || this.state.busy}
                        />
                    </div>
                }
            >
                <DocumentRequirementsPhotosModalContent
                    photoTypes={this.props.photoTypes}
                    photoTypeRequirements={this.props.photoTypeRequirements}
                    photos={this.props.photos}
                    fileName={this.props.fileName}
                    imgSrc={this.props.imgSrc}
                    mimeType={this.props.mimeType}
                    typeId={this.state.typeId}
                    description={this.state.description}
                    disabled={this.state.busy}
                    handleTypeIdOnChange={e =>
                        this.setState({ typeId: e.target.value })
                    }
                    handleDescriptionOnChange={e =>
                        this.setState({ description: e.target.value })
                    }
                />
            </Modal>
        );
    }

    handleSaveOnClick() {
        this.setState({ busy: true });
        this.props
            .handleSaveOnClick(this.state)
            .catch(() => this.setState({ busy: false }));
    }
}

export class DocumentSuccessfullyDownloadAllPhotoModal extends Component {
    static propTypes = {
        handleCloseOnClick: PropTypes.func.isRequired,
        header: PropTypes.node,
        download: PropTypes.shape({
            fileName: PropTypes.string.isRequired,
            url: PropTypes.string.isRequired,
        }),
        message: PropTypes.string,
        state: PropTypes.string.isRequired,
    };

    render() {
        const props = this.props;
        const closeDialog = props.state === 'completed';
        return (
            <Modal
                contentLabel='quote-export-modal'
                onCloseClick={props.handleCloseOnClick}
                header={props.header}
                showClose={closeDialog}
            >
                {props.state === 'downloading' && (
                    <LoadingSpinner size='large' />
                )}
                {props.state === 'completed' && (
                    <div style={{ padding: '10px 0 20px 0', fontSize: '15px' }}>
                        {props.message}
                        {props.download && (
                            <span>
                                &nbsp;Click
                                <a
                                    href={props.download.url}
                                    download={props.download.fileName}
                                    style={{ marginLeft: 5 }}
                                >
                                    here
                                </a>
                                &nbsp;to download
                            </span>
                        )}
                    </div>
                )}
            </Modal>
        );
    }
}

class DocumentRequirementsPhotosModalContent extends Component {
    static propTypes = {
        photoTypes: PropTypes.arrayOf(
            PropTypes.shape({
                id: PropTypes.string.isRequired,
                display: PropTypes.string.isRequired,
            })
        ).isRequired,
        fileName: PropTypes.string.isRequired,
        imgSrc: PropTypes.string.isRequired,
        mimeType: PropTypes.string.isRequired,
        typeId: PropTypes.string.isRequired,
        description: PropTypes.string.isRequired,
        handleTypeIdOnChange: PropTypes.func.isRequired,
        handleDescriptionOnChange: PropTypes.func.isRequired,
        currentPhotoTypeId: PropTypes.string,
        photoTypeRequirements: PropTypes.arrayOf(
            PropTypes.shape({
                id: PropTypes.number.isRequired,
                display: PropTypes.string.isRequired,
                requirement: PropTypes.shape({
                    id: PropTypes.string.isRequired,
                }),
            })
        ),
        photos: PropTypes.arrayOf(
            PropTypes.shape({
                id: PropTypes.number.isRequired,
                meetsClientRequirement: PropTypes.bool,
                metClientRequirementId: PropTypes.number,
                requirementTypeId: PropTypes.string,
                requirement: PropTypes.shape({
                    photo: PropTypes.object.isRequired,
                    uploadedBy: PropTypes.string,
                    description: PropTypes.string,
                    fileName: PropTypes.string,
                    url: PropTypes.string,
                    mimeType: PropTypes.string,
                }),
            })
        ),
        handlePullAndCleanChange: PropTypes.func,
        requiresPullAndClean: PropTypes.bool,
        action: PropTypes.string,
    };

    static defaultProps = {};

    render() {
        let photoTypes = this.mapPhotoTypesWithRequirements().filter(
            photoType =>
                this.props.photoTypeRequirements.some(
                    p => p.requirement.id === photoType.id
                ) ||
                this.props.photos.some(
                    p => p.requirement.photoTypeId === photoType.id
                ) ||
                (!photoType.deprecated &&
                    photoType.id !== 'LIFE_HEALTH_SAFETY_RISK')
        );
        // CS-2624
        photoTypes = this.applyLogicDeprecatedDamagedRepairedArea(photoTypes);
        const _photoTypeId = this.props.currentPhotoTypeId || this.props.typeId;

        const _handleRequiresPullAndClean = isChecked => {
            const { handlePullAndCleanChange } = this.props;
            if (
                handlePullAndCleanChange &&
                typeof handlePullAndCleanChange === 'function'
            ) {
                handlePullAndCleanChange(isChecked);
            }
        };

        const _isPullAndCleanBeforePhoto = photoTypeId => {
            return ['BLOWER_WHEEL_BEFORE', 'INDOOR_COIL_BEFORE'].includes(
                photoTypeId
            );
        };

        return (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
                <FormRow>
                    <FormElement>{this.renderImage()}</FormElement>
                    <FormElement
                        style={{
                            minWidth: 0,
                        }}
                    >
                        <FormRow>
                            <FormElement className={styles.photoFileName}>
                                <FormTextView
                                    bypassPreTag
                                    label='NAME'
                                    value={this.props.fileName}
                                />
                            </FormElement>
                        </FormRow>
                        <FormRow>
                            <FormElement
                                style={{
                                    minWidth: 0,
                                }}
                            >
                                <FormSelect
                                    disabled={this.props.currentPhotoTypeId}
                                    label='TYPE'
                                    options={photoTypes}
                                    value={
                                        this.props.currentPhotoTypeId ||
                                        this.props.typeId
                                    }
                                    onChange={opt => {
                                        this.props.handleTypeIdOnChange(opt.id);
                                        _handleRequiresPullAndClean(false);
                                    }}
                                />
                            </FormElement>
                        </FormRow>
                        <FormRow>
                            <FormElement>
                                <FormTextArea
                                    label='DESCRIPTION'
                                    value={this.props.description}
                                    onChange={
                                        this.props.handleDescriptionOnChange
                                    }
                                />
                            </FormElement>
                        </FormRow>
                        {_isPullAndCleanBeforePhoto(_photoTypeId) && (
                            <FormRow>
                                <FormElement>
                                    <Checkbox
                                        label='Requires Pull and Clean'
                                        id='document-upload-modal-pull-and-clean-option'
                                        value=''
                                        name='document-upload-modal-pull-and-clean-option'
                                        onChange={e =>
                                            _handleRequiresPullAndClean(
                                                e.target.checked
                                            )
                                        }
                                        checked={
                                            this.props.requiresPullAndClean
                                        }
                                        color='gray'
                                    />
                                </FormElement>
                            </FormRow>
                        )}
                        {_isPullAndCleanBeforePhoto(_photoTypeId) &&
                            this.props.requiresPullAndClean && (
                                <>
                                    <FormRow>
                                        <FormElement
                                            className={styles.pullCleanWarning}
                                        >
                                            <div>
                                                {this.getPullAndCleanWaring(
                                                    _photoTypeId
                                                )}
                                            </div>
                                        </FormElement>
                                    </FormRow>
                                    <FormRow>
                                        <FormElement
                                            className={styles.pullCleanWarning}
                                        >
                                            <div>
                                                {this.getPullAndCleanInstruction(
                                                    _photoTypeId
                                                )}
                                            </div>
                                        </FormElement>
                                    </FormRow>
                                </>
                            )}
                    </FormElement>
                </FormRow>
            </div>
        );
    }

    applyLogicDeprecatedDamagedRepairedArea(photoTypes) {
        let _photoTypes = [...photoTypes];
        const damageAreaInClientRequirement =
            this.props.photoTypeRequirements.some(
                photo => photo.requirement.id === 'DAMAGED_AREA'
            );
        const repairedAreaInClientRequirement =
            this.props.photoTypeRequirements.some(
                photo => photo.requirement.id === 'REPAIRED_AREA'
            );
        const damageAreaInWORequirement = this.props.photos.some(
            photo => photo.requirement.photoTypeId === 'DAMAGED_AREA'
        );
        const repairedAreaInWORequirement = this.props.photos.some(
            photo => photo.requirement.photoTypeId === 'REPAIRED_AREA'
        );
        if (damageAreaInClientRequirement || damageAreaInWORequirement) {
            _photoTypes = _photoTypes.filter(
                type => type.id !== 'AREA_PART_NEEDING_REPAIR'
            );
        } else {
            _photoTypes = _photoTypes.filter(
                type => type.id !== 'DAMAGED_AREA'
            );
        }
        if (repairedAreaInClientRequirement || repairedAreaInWORequirement) {
            _photoTypes = _photoTypes.filter(
                type => type.id !== 'AREA_PART_REPAIRED'
            );
        } else {
            _photoTypes = _photoTypes.filter(
                type => type.id !== 'REPAIRED_AREA'
            );
        }
        return _photoTypes;
    }

    getPullAndCleanWaring() {
        return `By selecting a Pull and Clean for this requirement, an automatic N/A photo will be applied to the After photo.`;
    }

    getPullAndCleanInstruction() {
        return `You must tag/identify this piece of equipment and select ‘Repair’ within the Equipment section of this Work Order before Submitting.`;
    }

    mapPhotoTypesWithRequirements() {
        const requiredPhotos = [];
        const metPhotos = [];
        const otherPhotos = [];

        this.props.photoTypes.forEach(photoType => {
            const matchedPhotoTypeRequirement = find(
                this.props.photoTypeRequirements,
                {
                    requirement: { id: photoType.id },
                }
            );
            const photos = this.props.photos?.filter(photo => !isVideo(photo));
            if (matchedPhotoTypeRequirement) {
                if (
                    find(photos, {
                        requirement: {
                            photoTypeId:
                                matchedPhotoTypeRequirement.requirement.id,
                        },
                    }) ||
                    find(photos, {
                        metProjectRequirementId: matchedPhotoTypeRequirement.id,
                    })
                ) {
                    return metPhotos.push({
                        ...photoType,
                        display: `${photoType.display} (Met)`,
                    });
                }
                return requiredPhotos.push({
                    ...photoType,
                    display: `${photoType.display} (Required)`,
                });
            }

            return otherPhotos.push(photoType);
        });

        return requiredPhotos.concat(metPhotos).concat(otherPhotos);
    }

    renderImage() {
        if (!this.props.imgSrc) {
            return <NotFoundImage />;
        }
        if (
            this.props.mimeType === 'image/png' ||
            this.props.mimeType === 'image/jpeg'
        ) {
            return (
                <img
                    style={{ width: '400px' }}
                    src={this.props.imgSrc}
                    alt=''
                />
            );
        }
        if (this.props.mimeType === 'application/pdf') {
            return <PDFImage />;
        }
        return <OtherImage />;
    }
}

class PDFImage extends PureComponent {
    static propTypes = {};

    static defaultProps = {};

    constructor(props) {
        super(props);
        this.style = {
            container: {
                width: '400px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
            },
            icon: {
                fontSize: '75px',
            },
        };
    }

    render() {
        return (
            <div style={this.style.container}>
                <i style={this.style.icon} className='fa fa-file-pdf-o' />
            </div>
        );
    }
}

class OtherImage extends PureComponent {
    static propTypes = {};

    static defaultProps = {};

    constructor(props) {
        super(props);
        this.style = {
            container: {
                width: '400px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
            },
            icon: {
                fontSize: '75px',
            },
        };
    }

    render() {
        return (
            <div style={this.style.container}>
                <i style={this.style.icon} className='fa fa-camera' />
            </div>
        );
    }
}

class NotFoundImage extends PureComponent {
    static propTypes = {};

    static defaultProps = {};

    constructor(props) {
        super(props);
        this.style = {
            container: {
                width: '400px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
            },
            icon: {
                fontSize: '75px',
            },
        };
    }

    render() {
        return (
            <div style={this.style.container}>
                <i style={this.style.icon} className='fa fa-ban' />
            </div>
        );
    }
}
